import {
  AlertCircle,
  Check,
  CheckCircle2,
  Clock,
  Clock3,
  Play,
  RotateCcw,
  UserX,
  X,
} from "lucide-react";
// New appointment status system utilities with updated enums
import React from "react";
import { i18n } from "@/lib/i18n";

export type AppointmentBookingStatus = "PENDING" | "CONFIRMED" | "DECLINED";
export type AppointmentOutcome = "IN_PROGRESS" | "NO_SHOW" | "RESCHEDULED" | "COMPLETED";

export interface StatusInfo {
  icon: React.ComponentType<{ className?: string }>;
  text: string;
  iconColor: string;
  backgroundColor: string;
  borderColor: string;
  textColor: string;
  dotColor: string;
}

/**
 * Get comprehensive booking status information
 */
export function getBookingStatusInfo(status: AppointmentBookingStatus): StatusInfo {
  const t = i18n.getFixedT(null, "appointments");

  switch (status) {
    case "CONFIRMED":
      return {
        icon: Check,
        text: t("status.accept"),
        iconColor: "text-blue-600 dark:text-blue-400",
        backgroundColor: "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800/30",
        borderColor: "border-blue-200 dark:border-blue-800/30",
        textColor: "text-blue-800 dark:text-blue-200",
        dotColor: "bg-blue-500 dark:bg-blue-400",
      };
    case "DECLINED":
      return {
        icon: X,
        text: t("status.declined"),
        iconColor: "text-red-600 dark:text-red-400",
        backgroundColor: "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800/30",
        borderColor: "border-red-200 dark:border-red-800/30",
        textColor: "text-red-800 dark:text-red-200",
        dotColor: "bg-red-500 dark:bg-red-400",
      };
    case "PENDING":
      return {
        icon: Clock3,
        text: t("status.pending"),
        iconColor: "text-yellow-600 dark:text-yellow-400",
        backgroundColor:
          "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800/30",
        borderColor: "border-yellow-200 dark:border-yellow-800/30",
        textColor: "text-yellow-800 dark:text-yellow-200",
        dotColor: "bg-yellow-500 dark:bg-yellow-400",
      };
    default:
      return {
        icon: AlertCircle,
        text: t("status.unknown"),
        iconColor: "text-muted-foreground",
        backgroundColor: "bg-muted border-border",
        borderColor: "border-border",
        textColor: "text-foreground",
        dotColor: "bg-muted-foreground",
      };
  }
}

/**
 * Get comprehensive outcome status information
 */
export function getOutcomeStatusInfo(outcome: AppointmentOutcome | null | undefined): StatusInfo {
  const t = i18n.getFixedT(null, "appointments");

  switch (outcome) {
    case "COMPLETED":
      return {
        icon: CheckCircle2,
        text: t("status.completed"),
        iconColor: "text-green-600 dark:text-green-400",
        backgroundColor:
          "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800/30",
        borderColor: "border-green-200 dark:border-green-800/30",
        textColor: "text-green-800 dark:text-green-200",
        dotColor: "bg-green-500 dark:bg-green-400",
      };
    case "NO_SHOW":
      return {
        icon: UserX,
        text: t("status.noShow"),
        iconColor: "text-red-600 dark:text-red-400",
        backgroundColor: "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800/30",
        borderColor: "border-red-200 dark:border-red-800/30",
        textColor: "text-red-800 dark:text-red-200",
        dotColor: "bg-red-500 dark:bg-red-400",
      };
    case "RESCHEDULED":
      return {
        icon: RotateCcw,
        text: t("status.rescheduled"),
        iconColor: "text-orange-600 dark:text-orange-400",
        backgroundColor:
          "bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800/30",
        borderColor: "border-orange-200 dark:border-orange-800/30",
        textColor: "text-orange-800 dark:text-orange-200",
        dotColor: "bg-orange-500 dark:bg-orange-400",
      };
    case "IN_PROGRESS":
      return {
        icon: Play,
        text: t("status.inProgress"),
        iconColor: "text-purple-600 dark:text-purple-400",
        backgroundColor:
          "bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800/30",
        borderColor: "border-purple-200 dark:border-purple-800/30",
        textColor: "text-purple-800 dark:text-purple-200",
        dotColor: "bg-purple-500 dark:bg-purple-400",
      };
    default:
      return {
        icon: Clock,
        text: t("details.labels.notStarted"),
        iconColor: "text-muted-foreground",
        backgroundColor: "bg-muted border-border",
        borderColor: "border-border",
        textColor: "text-foreground",
        dotColor: "bg-muted-foreground",
      };
  }
}

// Status value arrays for dropdown generation using GraphQL values
export const BOOKING_STATUS_VALUES: AppointmentBookingStatus[] = [
  "CONFIRMED",
  "DECLINED",
  "PENDING",
];

export const OUTCOME_STATUS_VALUES: (AppointmentOutcome | null)[] = [
  "COMPLETED",
  "NO_SHOW",
  "RESCHEDULED",
  "IN_PROGRESS",
  null,
];
